/**
 * OTStudyHome - Omni-Triage clinical study home page
 *
 * Shows all batches with per-reader progress, instructions link,
 * and quick navigation to start labeling.
 *
 * URL: /labeling/ot
 */

import { useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { useProjectsQuery, useProgressQuery } from "../../hooks/useLabelingQueries";
import { FaChevronRight, FaUser } from "../../utils/icons";

const OT_PREFIX = "ot-v1-";

interface BatchInfo {
  batchNum: string; // "batch01"
  batchLabel: string; // "Batch 1"
  readers: {
    reader: string; // "readerA"
    readerLabel: string; // "Reader A"
    projectId: string; // "ot-v1-batch01-readerA"
    totalCases: number;
    totalLabels: number;
  }[];
}

function BatchProgressBar({ projectId }: { projectId: string }) {
  const { data } = useProgressQuery(projectId);
  const pct = data?.completion_percentage ?? 0;
  const isComplete = pct >= 100;

  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 rounded-full bg-surface-2 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${
            isComplete ? "bg-signal" : "bg-primary"
          }`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span
        className={`text-xs tabular-nums min-w-[3ch] text-right ${
          isComplete ? "text-signal-text-tint" : "text-fg-muted"
        }`}
      >
        {pct.toFixed(0)}%
      </span>
    </div>
  );
}

function BatchCard({ batch, onStart }: { batch: BatchInfo; onStart: (projectId: string) => void }) {
  return (
    <div className="panel-premium rounded border border-border/50 overflow-hidden">
      {/* Header */}
      <div className="px-5 py-3 border-b border-border/30 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-white">{batch.batchLabel}</h3>
        <span className="text-xs text-fg-muted">{batch.readers[0]?.totalCases ?? 0} studies</span>
      </div>

      {/* Readers */}
      <div className="divide-y divide-border/20">
        {batch.readers.map(({ reader, readerLabel, projectId }) => (
          <div key={reader} className="px-5 py-3 flex items-center gap-3">
            <div className="flex items-center gap-2 w-24 shrink-0">
              <FaUser className="w-3 h-3 text-fg-muted" />
              <span className="text-xs text-fg-muted">{readerLabel}</span>
            </div>
            <div className="flex-1">
              <BatchProgressBar projectId={projectId} />
            </div>
            <button
              onClick={() => onStart(projectId)}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium
                         bg-primary/10 text-signal-text-tint hover:bg-primary/20 transition-colors"
            >
              Start
              <FaChevronRight className="w-2.5 h-2.5" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export function OTStudyHome() {
  const navigate = useNavigate();
  const { data: projectsData, isLoading } = useProjectsQuery();

  const batches = useMemo(() => {
    if (!projectsData?.projects) return [];

    // Only include batch-reader projects (e.g. ot-v1-batch01-readerA), not trial/other variants
    const otProjects = projectsData.projects.filter((p) =>
      /^ot-v1-batch\d+-reader[A-Z]$/.test(p.project_id),
    );

    // Group by batch
    const batchMap = new Map<string, BatchInfo>();
    for (const project of otProjects) {
      // Parse: ot-v1-batch01-readerA
      const parts = project.project_id.replace(OT_PREFIX, "").split("-");
      const batchNum = parts[0]; // "batch01"
      const reader = parts[1]; // "readerA"

      if (!batchMap.has(batchNum)) {
        const num = parseInt(batchNum.replace("batch", ""), 10);
        batchMap.set(batchNum, {
          batchNum,
          batchLabel: `Batch ${num}`,
          readers: [],
        });
      }

      const readerNum = reader.replace("reader", "");
      batchMap.get(batchNum)!.readers.push({
        reader,
        readerLabel: `Reader ${readerNum}`,
        projectId: project.project_id,
        totalCases: project.total_cases,
        totalLabels: project.total_labels,
      });
    }

    // Sort batches and readers
    return Array.from(batchMap.values())
      .sort((a, b) => a.batchNum.localeCompare(b.batchNum))
      .map((batch) => ({
        ...batch,
        readers: batch.readers.sort((a, b) => a.reader.localeCompare(b.reader)),
      }));
  }, [projectsData]);

  const totalStudies = batches.reduce((sum, b) => sum + (b.readers[0]?.totalCases ?? 0), 0);
  const totalProjects = batches.reduce((sum, b) => sum + b.readers.length, 0);

  const handleStart = (projectId: string) => {
    navigate(`/labeling?project=${encodeURIComponent(projectId)}`);
  };

  return (
    <>
      <Helmet>
        <title>Omni-Triage Study | a2z</title>
      </Helmet>

      <div className="h-full overflow-auto text-white page-premium">
        <div className="max-w-5xl mx-auto px-6 py-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-2xl font-bold text-white">
                a2z-Omni-Triage
                <span className="ml-2 text-xs font-normal text-fg-muted align-middle">v1</span>
              </h1>
            </div>
            <p className="text-fg-muted text-sm">
              Clinical validation — 12-condition abdominopelvic CT triage
            </p>
            <div className="flex items-center gap-4 mt-3 text-xs text-fg-muted">
              <span>{totalStudies} studies</span>
              <span className="text-fg-muted">|</span>
              <span>{batches.length} batches</span>
              <span className="text-fg-muted">|</span>
              <span>{totalProjects} reader projects</span>
              <span className="text-fg-muted">|</span>
              <span>12 conditions per study</span>
            </div>
          </div>

          {/* Conditions */}
          <div className="mb-8 panel-premium rounded border border-border/50 p-5">
            <h2 className="text-xs font-semibold text-fg-muted uppercase tracking-wider mb-3">
              Conditions
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1.5">
              {[
                "Moderate or Large Layering Pleural Effusion",
                "Pneumothorax",
                "Complex Pleural Collection",
                "Bowel Perforation",
                "Moderate or Severe Hydronephrosis",
                "Large Abdominal Aortic Aneurysm",
                "Rib Fracture",
                "Vertebral Fracture",
                "Non-sacral Pelvic Fracture",
                "Sacral or Coccygeal Fracture",
                "Measurable Soft Tissue Hematoma",
                "Soft Tissue Abscess",
              ].map((condition, i) => (
                <div key={condition} className="flex items-center gap-2">
                  <span className="text-[10px] text-fg-muted tabular-nums w-4 text-right">
                    {i + 1}
                  </span>
                  <span className="text-xs text-fg">{condition}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Batches grid */}
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
            </div>
          ) : batches.length === 0 ? (
            <div className="text-center py-16">
              <div className="text-fg-muted mb-2">No OT batches found</div>
              <div className="text-fg-muted text-xs">
                Upload worklists with prefix "{OT_PREFIX}" to get started
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {batches.map((batch) => (
                <BatchCard key={batch.batchNum} batch={batch} onStart={handleStart} />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
